import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import Modal from 'react-modal';
import {sendGetRequest,formatVolume} from "../../../../helper/Helper";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import dayjs from 'dayjs';

const timeframes = {
  '24H': 0.5,
  '1W': 0.8,
  '1M': 1,
  '3M': 1.1,
  '6M': 1.2,
  '1Y': 1.3,
  'ALL': 1.4,
};

Modal.setAppElement('#root');
function parseVol(liqStr) {
  return parseFloat(liqStr.replace(/Liq.\s*/i, '').replace(/K/i, '')) * (liqStr.includes('K') ? 1000 : 1);
}

const VolumeBableChart = () => {
  const canvasRef = useRef();
  const [timeframe, setTimeframe] = useState('3M');
  const [selectedToken, setSelectedToken] = useState(null);
  const simulationRef = useRef(null);
  const [originalTokens, setOriginalTokens] = useState([]);

  useEffect(() => {
  const getOriginalTokens = async () => {
    const response = await sendGetRequest('pairs-volumes/stats/volume-24h');
    if (response && response.status === 200) {
      setOriginalTokens(response.data.records.map(item => {
        const name = item.base_symbol === "pTGC" ? item.qoute_symbol : (item.base_symbol === "" ? item.base_name : item.base_symbol);

        const imgPath = item.base_symbol === "pTGC"
          ? './coinimages/' + item.qoute_address.toLowerCase() + '.png'
          : './coinimages/' + item.base_address.toLowerCase() + '.png';

        return {
          ...item,
          name,
          liq: formatVolume(item.volume_24hr.toFixed(2)),
          img: imgPath,
          logoFallback: name?.split(' ')[0]?.[0] || '?',  // 👈 First letter fallback
        };
      }));

    }
  };
  getOriginalTokens();
}, []);

  const totalLiquidity = originalTokens.reduce((sum, t) => sum + t.volume_24hr, 0);

  const handleClose = () => {
    document.body.classList.toggle('listOpen');
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let W = window.innerWidth;
    let H = window.innerHeight;
    canvas.width = W;
    canvas.height = H;

    const resize = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width = W;
      canvas.height = H;
      if (simulationRef.current) {
        simulationRef.current.force('center', d3.forceCenter(W / 2, H / 2));
        simulationRef.current.alpha(1).restart();
      }
    };
    window.addEventListener('resize', resize);

    const liqs = originalTokens.map(t => t.volume_24hr);
    const minVol = Math.min(...liqs);
    const maxVol = Math.max(...liqs);
    const scaleArea = d3.scalePow()
      .exponent(0.7)
      .domain([minVol, maxVol])
      .range([Math.PI * 40 * 40, Math.PI * 140 * 140]);

    const isMobile = W < 768;
    const isShortTerm = timeframe === '24H' || timeframe === '1W';
    const mobileScale = isMobile
      ? (isShortTerm ? 0.7 : 0.9)
      : (isShortTerm ? 1.5 : 1.2);
    const tokensWithRadii = originalTokens.map(t => {
      const area = scaleArea(t.volume_24hr);
      const r = Math.sqrt(area / Math.PI) * timeframes[timeframe] * 0.7 * mobileScale;
      return { ...t, r };
    });

    const nodes = tokensWithRadii.map(d => ({
      ...d,
      x: Math.random() * W,
      y: Math.random() * H,
    }));

    const simulation = d3.forceSimulation(nodes)
      .velocityDecay(0.2)
      .force('charge', d3.forceManyBody().strength(5))
      .force('center', d3.forceCenter(W / 2, H / 2))
      .force('collision', d3.forceCollide().radius(d => d.r + 2).strength(1))
      .alphaDecay(0)
      .on('tick', () => {
        ctx.clearRect(0, 0, W, H);
        nodes.forEach(b => {
          const gradient = ctx.createLinearGradient(b.x - b.r, b.y, b.x + b.r, b.y);
          gradient.addColorStop(0, '#F8F192');
          gradient.addColorStop(1, '#9D721F');
          ctx.beginPath();
          ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(255,255,255,0.05)';
          ctx.fill();
          ctx.lineWidth = 6;
          ctx.strokeStyle = gradient;
          ctx.stroke();

          const textGradient = ctx.createLinearGradient(b.x - b.r, b.y, b.x + b.r, b.y);
          textGradient.addColorStop(0, '#F8F192');
          textGradient.addColorStop(1, '#9D721F');
          ctx.fillStyle = textGradient;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          let fontSize = b.r / 2.5;
          ctx.font = `${fontSize}px good_times`;
          const textWidth = ctx.measureText(b.name).width;
          if (textWidth > b.r * 1.5) {
            fontSize = Math.max(fontSize * (b.r * 1.5 / textWidth), 8);
            ctx.font = `${fontSize}px good_times`;
          }

          ctx.fillText(b.name, b.x, b.y - b.r * 0.25);
          ctx.fillStyle = 'rgba(255,255,255,0.7)';
          ctx.font = `${Math.max(b.r / 4.5, 7)}px good_times`;
          ctx.fillText(b.liq, b.x, b.y + b.r * 0.25);
        });
      });

    simulation.alpha(1).restart();
    simulationRef.current = simulation;

    const handleClick = (e) => {
      const { left, top } = canvas.getBoundingClientRect();
      const x = e.clientX - left;
      const y = e.clientY - top;
      const clicked = nodes.find(n => Math.hypot(n.x - x, n.y - y) <= n.r);
      if (clicked) setSelectedToken(clicked);
    };

    canvas.addEventListener('click', handleClick);
    return () => {
      simulation.stop();
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('click', handleClick);
    };
  }, [timeframe, originalTokens]);

  return (
    <>
      <div className="modh2Dcolor">
        <h2 className="h2Dcolor d-flex justify-content-between" onClick={handleClose}>
          Volume List
          <button className="nobg f14 text-white">
            <i className="fa-solid fa-arrow-right"></i>
          </button>
        </h2>
      </div>
      <div className="timeframeList statsSN">
        <h2 className=" d-flex justify-content-end">
          <button className="nobg f14 text-white" onClick={handleClose}>
            <i className="fa-solid fa-xmark"></i>
            <i className="fa-solid fa-angle-right"></i>
          </button>
        </h2>
        <div className="height tablecontainer">
          <table className="fixedheadertable" style={{ width: '100%', color: '#fff', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th className="upper goldenText good_times">Pairs</th>
                <th className="upper goldenText good_times">Volume</th>
              </tr>
            </thead>
            <tbody>
              {originalTokens.map((token, index) => (
                <tr key={index} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <td style={{ padding: '8px', display: 'flex', alignItems: 'center' }}>
                    <div className="logoImgm">
                        <div className="token-img-wrapper">
                          <img
                            src={token.img}
                            alt={token.name}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const fallbackEl = e.target.nextElementSibling;
                              if (fallbackEl) fallbackEl.style.display = 'flex';
                            }}
                            style={{
                              width: 30,
                              height: 30,
                              borderRadius: '50%',
                            }}
                            baseaddress={token.base_address.toLowerCase()}
                            qouteaddress={token.qoute_address.toLowerCase()}
                          />
                          <div className="fallback-letter" style={{ display: 'none' }}>
                            {token.logoFallback}
                          </div>
                        </div>
                        <img
                          src="./images/logoIcon.svg"
                          alt="UFO"
                          style={{
                            width: 30,
                            height: 30,
                            borderRadius: '50%',
                            marginLeft: '-8px',
                            marginRight: '8px',
                            position: 'relative',
                          }}
                        />
                      </div>
                    <span className="goldenText good_times upper">{token.name} / PTGC</span>
                  </td>
                  <td style={{ padding: '8px' }}>${parseFloat(token.volume_24hr).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="totalHere d-flex justify-content-between gap-2 mt-3">
          <p className="upper f14 mb-0 goldenText good_times">Total Volume:</p>
          <p className="upper f14 mb-0"><b>${parseFloat(totalLiquidity).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b></p>
        </div>
      </div>
      <canvas ref={canvasRef} style={{position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: -1, display: 'block' }} />
    </>
  );
};

export default VolumeBableChart;
